import Async from '../Async';

class Attendees {
    /**
     * @method constructor
     */
    constructor() {
        // Declare and initialize
        this.modal           = $('#manage-attendees-modal');
        this.search          = this.modal.find('[data-attendee-search-input]');
        this.items           = this.modal.find('[data-attendee-item]');
        this.letterButtons   = this.modal.find('[data-attendee-letter]');
        this.noResults       = this.modal.find('[data-attendee-no-results]');
        this.activeLetter    = '';
        this.preserveFilters = false;
        this.async           = new Async();
        this.messageDelay    = 2500;

        // Nothing to manage on this page
        if (!this.modal.length)
            return;

        this.init();
    }

    /**
     * Bind attendee-management interactions.
     *
     * @method init
     * @since 0.0.1
     * @returns void
     */
    init() {
        // Search the existing attendee items
        this.search.on('input', () => {
            this.filter();
        });

        // Filter attendees by last-name initial
        this.letterButtons.on('click', (event) => {
            this.setLetter($(event.currentTarget));
        });

        // Open an attendee when their card is clicked
        this.items.on('click', (event) => {
            event.preventDefault();
            event.stopPropagation();
            this.openDetails($(event.currentTarget));
        });

        // Focus the search when the Manage Attendees modal opens
        this.modal.on('shown.bs.modal', () => {
            this.search.trigger('focus');
        });

        // Clear filter state only when the attendee manager is actually closed
        this.modal.on('hidden.bs.modal', () => {
            if (this.preserveFilters) {
                this.preserveFilters = false;
                return;
            }

            this.resetFilters();
        });

        // Return to the attendee list through the same modal lifecycle
        $(document).on('click', '[data-attendee-back]', (event) => {
            this.backToManage($(event.currentTarget));
        });

        // Check an attendee in
        $(document).on('click', '[data-attendee-checkin-button]', (event) => {
            let button = $(event.currentTarget);

            if (button.prop('disabled'))
                return;

            this.updateCheckIn(button.closest('[data-attendee-checkin]'));
        });

        // Undo an attendee check-in
        $(document).on('click', '[data-attendee-checkin-undo]', (event) => {
            let link = $(event.currentTarget);

            if (link.prop('disabled'))
                return;

            this.updateCheckIn(link.closest('[data-attendee-checkin]'), true);
        });
    }

    /**
     * Filter rendered attendee items.
     *
     * @method filter
     * @since 0.0.1
     * @returns void
     */
    filter() {
        // Declare and initialize
        let query   = String(this.search.val() || '').trim().toLowerCase();
        let letter  = this.activeLetter;
        let visible = 0;

        // Run through the rendered attendee items
        this.items.each(function () {
            let item          = $(this);
            let haystack      = item.attr('data-attendee-search') || '';
            let letters       = (item.attr('data-attendee-last-name-letters') || '').split(' ').filter(Boolean);
            let matchesSearch = !query || haystack.includes(query);
            let matchesLetter = !letter || letters.includes(letter);
            let matches       = matchesSearch && matchesLetter;

            item.closest('[data-attendee-item-wrap]').toggle(matches);

            if (matches)
                visible++;
        });

        // Show the empty state when nothing matches
        this.noResults.toggleClass('d-none', 0 < visible);
    }

    /**
     * Set the active last-name letter filter.
     *
     * @method setLetter
     * @since 0.0.1
     * @param object button is the clicked alphabet button
     * @returns void
     */
    setLetter(button) {
        // Set the active letter
        this.activeLetter = String(button.attr('data-attendee-letter') || '').toUpperCase();

        // Reset the button states
        this.letterButtons
            .removeClass('btn-primary')
            .addClass('btn-outline-secondary')
            .attr('aria-pressed', 'false');

        // Highlight the active filter
        button
            .removeClass('btn-outline-secondary')
            .addClass('btn-primary')
            .attr('aria-pressed', 'true');

        // Apply both filters together
        this.filter();
    }

    /**
     * Reset the search and alphabet filters.
     *
     * @method resetFilters
     * @since 0.0.1
     * @returns void
     */
    resetFilters() {
        // Reset the query and active letter
        this.search.val('');
        this.activeLetter = '';

        // Restore the default alphabet button state
        this.letterButtons
            .removeClass('btn-primary')
            .addClass('btn-outline-secondary')
            .attr('aria-pressed', 'false');

        this.letterButtons.filter('[data-attendee-letter=""]')
            .removeClass('btn-outline-secondary')
            .addClass('btn-primary')
            .attr('aria-pressed', 'true');

        // Show the full list again
        this.filter();
    }

    /**
     * Move from Manage Attendees to one attendee detail modal.
     *
     * @method openDetails
     * @since 0.0.1
     * @param object item is the clicked attendee item
     * @returns void
     */
    openDetails(item) {
        let target = $(item.attr('data-target'));

        if (!target.length)
            return;

        // Preserve the current list filters while viewing attendee details
        this.preserveFilters = true;

        this.modal.one('hidden.bs.modal', () => {
            target.modal('show');
        });

        this.modal.modal('hide');
    }

    /**
     * Move from attendee details back to Manage Attendees.
     *
     * @method backToManage
     * @since 0.0.1
     * @param object button is the clicked Back button
     * @returns void
     */
    backToManage(button) {
        let details = button.closest('.attendee-details-modal');

        if (!details.length)
            return;

        details.one('hidden.bs.modal', () => {
            this.modal.modal('show');
        });

        details.modal('hide');
    }

    /**
     * Update attendee check-in state through the existing check-in provider.
     *
     * @method updateCheckIn
     * @since 0.0.1
     * @param object container is the attendee check-in card
     * @param bool undo indicates whether the check-in should be removed
     * @returns void
     */
    updateCheckIn(container, undo = false) {
        let button     = container.find('[data-attendee-checkin-button]');
        let undoLink   = container.find('[data-attendee-checkin-undo]');
        let status     = container.find('[data-attendee-checkin-status]');
        let priorDate  = container.attr('data-attendee-checked-in-display') || '';
        let priorShort = container.attr('data-attendee-checked-in-short-display') || '';
        let priorState = undo;

        button.prop('disabled', true).attr('aria-busy', 'true');
        undoLink.prop('disabled', true);
        this.renderWorking(status, undo);

        this.async.post('check-in', {
            params: {
                code:       button.attr('data-attendee-code'),
                event_id:   button.attr('data-event-id'),
                recur_date: button.attr('data-recur-date'),
                undo:       undo ? 1 : 0
            },
            success: {
                callback: (response) => {
                    this.handleCheckInResponse(container, response, undo, priorState, priorDate, priorShort);
                }
            },
            fail: {
                callback: () => {
                    this.showMessage(status, 'Unable to update check-in.', 'danger', () => {
                        this.renderCheckInState(container, priorState, priorDate, priorShort);
                    });
                }
            }
        });
    }

    /**
     * Handle the normalized provider response for check-in/check-out actions.
     *
     * @method handleCheckInResponse
     * @since 0.0.1
     * @returns void
     */
    handleCheckInResponse(container, response, undo, priorState, priorDate, priorShort) {
        let status = container.find('[data-attendee-checkin-status]');

        if (!response || !response.success) {
            this.showMessage(
                status,
                response && response.message ? response.message : 'Unable to update check-in.',
                'danger',
                () => {
                    this.renderCheckInState(container, priorState, priorDate, priorShort);
                }
            );
            return;
        }

        let result    = response.data || {};
        let date      = result.checked_in_at_display || '';
        let shortDate = result.checked_in_at_short_display || '';
        let checkedIn = !undo;

        this.showMessage(
            status,
            response.message || (undo ? 'Check-in removed.' : 'Attendee checked in.'),
            'success',
            () => {
                this.renderCheckInState(container, checkedIn, date, shortDate);
            }
        );
    }

    /**
     * Render a temporary working message.
     *
     * @method renderWorking
     * @since 0.0.1
     * @returns void
     */
    renderWorking(status, undo = false) {
        status.stop(true, true).show().empty();

        $('<span>', {
            class: 'text-muted',
            text:  undo ? 'Removing check-in...' : 'Checking in...'
        }).appendTo(status);
    }

    /**
     * Briefly show request feedback before restoring the normal check-in state.
     *
     * @method showMessage
     * @since 0.0.1
     * @returns void
     */
    showMessage(status, message, type, callback) {
        status.stop(true, true).show().empty();

        $('<span>', {
            class: 'text-' + type + ('success' === type ? ' font-weight-bold' : ''),
            text:  message
        }).appendTo(status);

        window.setTimeout(() => {
            status.fadeOut(300, () => {
                callback();
                status.show();
            });
        }, this.messageDelay);
    }

    /**
     * Render the same check-in state shown after a page refresh.
     *
     * @method renderCheckInState
     * @since 0.0.1
     * @returns void
     */
    renderCheckInState(container, checkedIn, date = '', shortDate = '') {
        let status       = container.find('[data-attendee-checkin-status]');
        let button       = container.find('[data-attendee-checkin-button]');
        let label        = button.find('[data-attendee-checkin-button-label]');
        let defaultIcon  = button.find('[data-attendee-checkin-icon-default]');
        let checkedIcon  = button.find('[data-attendee-checkin-icon-checked]');
        let undoLink     = container.find('[data-attendee-checkin-undo]');

        status.stop(true, true).show().empty();
        button.removeAttr('aria-busy');
        undoLink.prop('disabled', false);

        if (!checkedIn) {
            $('<span>', {
                class: 'text-muted',
                text:  'Not Checked In'
            }).appendTo(status);

            container.attr('data-attendee-checked-in-display', '');
            container.attr('data-attendee-checked-in-short-display', '');
            button.removeClass('btn-success').addClass('btn-primary').prop('disabled', false);
            label.text('Check In');
            defaultIcon.removeClass('d-none');
            checkedIcon.addClass('d-none');
            undoLink.addClass('d-none');
            this.syncListCheckIn(container, false);
            return;
        }

        $('<span>', {
            class: 'text-success font-weight-bold',
            text:  'Checked In'
        }).appendTo(status);

        $('<div>', {
            class: 'small text-muted mt-1',
            text:  date
        }).appendTo(status);

        container.attr('data-attendee-checked-in-display', date);
        container.attr('data-attendee-checked-in-short-display', shortDate);
        button.removeClass('btn-primary').addClass('btn-success').prop('disabled', true);
        label.text('Checked In');
        defaultIcon.addClass('d-none');
        checkedIcon.removeClass('d-none');
        undoLink.removeClass('d-none');
        this.syncListCheckIn(container, true, shortDate);
    }

    /**
     * Keep the Manage Attendees list in sync with check-in changes.
     *
     * @method syncListCheckIn
     * @since 0.0.1
     * @param object container is the attendee check-in card
     * @param bool checkedIn indicates whether the attendee is checked in
     * @param string date is the short formatted check-in date/time
     * @returns void
     */
    syncListCheckIn(container, checkedIn, date = '') {
        // Declare and initialize
        let attendeeId = container.attr('data-attendee-id');
        let item       = this.modal.find('[data-attendee-item][data-attendee-id="' + attendeeId + '"]');

        // Nothing to update if the attendee is not in the list
        if (!item.length)
            return;

        // Pull the check-in display elements
        let indicator = item.find('[data-attendee-list-checkin-indicator]');
        let dateValue = item.find('[data-attendee-list-checkin-date-value]');

        // Toggle the checked-in details
        indicator.toggleClass('d-none', !checkedIn);
        dateValue.text(checkedIn ? date : '');
    }
}

export default Attendees;
